import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type ColumnDef,
  type ColumnFiltersState,
  type ColumnPinningState,
  type ColumnSizingInfoState,
  type ColumnSizingState,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type Row,
  type RowSelectionState,
  type SortingState,
  type Table,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table';

import { DEFAULT_COLUMN_SIZE } from './constants';
import { useScrollToColumn } from './hooks';
import type { ColumnConfig, ParsedChildren, TableContextValue } from './types';
import { estimateColumnWidth } from './utils/estimate-column-width';
import { getFilterFn } from './utils/filtering';

// =============================================================================
// Cache Helpers
// =============================================================================

const CACHE_PREFIX = 'data-table';

const loadFromCache = <T>(tableId: string, key: string): T | undefined => {
  try {
    const stored = localStorage.getItem(`${CACHE_PREFIX}:${tableId}:${key}`);
    return stored ? (JSON.parse(stored) as T) : undefined;
  } catch {
    return undefined;
  }
};

const saveToCache = <T>(tableId: string, key: string, value: T): void => {
  try {
    localStorage.setItem(`${CACHE_PREFIX}:${tableId}:${key}`, JSON.stringify(value));
  } catch {
    // Ignore storage errors
  }
};

// =============================================================================
// Column Definition Builder
// =============================================================================

/**
 * Transforms parsed `ColumnConfig` objects into TanStack `ColumnDef` objects.
 *
 * Column sizing follows a priority chain:
 * 1. Explicit `width` prop on `<DataTable.Column>` → `config.size`
 * 2. Auto-estimated from header text via `estimateColumnWidth()`
 * 3. `defaultColumnSize` (from `<DataTable>` prop or `DEFAULT_COLUMN_SIZE`)
 */
const buildColumnDefs = <TData>(
  columns: ColumnConfig<TData>[],
  defaultColumnSize: number = DEFAULT_COLUMN_SIZE
): ColumnDef<TData>[] => {
  return columns.map((config) => {
    const columnDef: ColumnDef<TData> = {
      id: config.id,
      // Sizing
      size: config.size ?? estimateColumnWidth(config.headerContent) ?? defaultColumnSize,
      minSize: config.minSize ?? 50,
      maxSize: config.maxSize ?? 1500,
      enableResizing: config.enableResizing ?? true,
      // Sorting
      enableSorting: config.enableSorting ?? true,
      sortDescFirst: config.sortDescFirst ?? false,
      // Filtering
      enableColumnFilter: config.enableFiltering ?? false,
      // Use explicit filterFn if provided, otherwise derive from filterType
      filterFn: (config.filterFn ??
        (config.filterType
          ? getFilterFn(config.filterType)
          : undefined)) as ColumnDef<TData>['filterFn'],
      // Meta for custom rendering
      meta: {
        headerContent: config.headerContent,
        customHeaderRenderer: config.customHeaderRenderer,
        cellRenderer: config.cellRenderer,
        filterType: config.filterType,
        filterOptions: config.filterOptions,
        filterPlaceholder: config.filterPlaceholder,
        pinned: config.pinned,
        growFirst: config.growFirst,
      },
    };

    // Add accessor
    if (config.accessorFn) {
      (columnDef as ColumnDef<TData> & { accessorFn: typeof config.accessorFn }).accessorFn =
        config.accessorFn;
    } else if (config.accessorKey) {
      (columnDef as ColumnDef<TData> & { accessorKey: string }).accessorKey = config.accessorKey;
    }

    // Add custom sorting function
    if (config.sortingFn) {
      columnDef.sortingFn = config.sortingFn as ColumnDef<TData>['sortingFn'];
    }

    return columnDef;
  });
};

// =============================================================================
// Hook Options
// =============================================================================

interface UseTableStateOptions<TData> {
  tableId: string;
  data: TData[];
  parsedChildren: ParsedChildren<TData>;
  isMobile: boolean;
  widthMode: 'auto' | 'full';
  defaultColumnSize?: number;
  /**
   * Function to derive a stable, unique row ID from each data item.
   * This is critical for virtualization and sorting to work correctly.
   */
  getRowId?: (row: TData, index: number) => string;
  /** Message shown in the empty tbody row when there are no rows */
  emptyMessage?: string;
}

// =============================================================================
// Return Type
// =============================================================================

/**
 * Result from useTableState hook.
 */
export interface UseTableStateResult<TData> {
  /** Context value to be provided to TableProvider */
  contextValue: TableContextValue<TData>;
  /** TanStack Table instance */
  table: Table<TData>;
}

// =============================================================================
// Main Hook
// =============================================================================

/**
 * Creates and manages TanStack Table instance.
 *
 * Supports both controlled and uncontrolled modes - TanStack Table handles this natively:
 * - Controlled: Pass `value` and `onChange` via config components
 * - Uncontrolled: Pass `initial` or nothing - TanStack manages state internally
 */
export const useTableState = <TData>({
  tableId,
  data,
  parsedChildren,
  isMobile,
  widthMode,
  defaultColumnSize: defaultColumnSizeProp,
  getRowId: getRowIdProp,
  emptyMessage = 'No data available',
}: UseTableStateOptions<TData>): UseTableStateResult<TData> => {
  const effectiveDefaultColumnSize = defaultColumnSizeProp ?? DEFAULT_COLUMN_SIZE;
  const {
    columns,
    settings,
    selection,
    filter,
    globalFilter: globalFilterConfig,
    sorting: sortingConfig,
    scrollTo = {},
  } = parsedChildren;

  // Create ref for table DOM element access (used by ColumnOverlay)
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ---------------------------------------------------------------------------
  // Determine controlled vs uncontrolled
  // ---------------------------------------------------------------------------

  const isFilterControlled = filter.value !== undefined;
  const isGlobalFilterControlled = globalFilterConfig.value !== undefined;
  const isSortingControlled = sortingConfig.value !== undefined;
  const isSelectionControlled = selection.value !== undefined;

  // ---------------------------------------------------------------------------
  // Internal state for uncontrolled mode
  // TanStack needs state + onChange even for uncontrolled - we provide it
  // ---------------------------------------------------------------------------

  // Sorting: controlled value > initial > cache > empty
  const [uncontrolledSorting, setUncontrolledSorting] = useState<SortingState>(() => {
    if (sortingConfig.initial !== undefined) return sortingConfig.initial;
    if (settings.cache.sorting) return loadFromCache<SortingState>(tableId, 'sorting') ?? [];
    return [];
  });

  // Filters: controlled value > initial > empty
  const [uncontrolledFilters, setUncontrolledFilters] = useState<ColumnFiltersState>(
    () => filter.initial ?? []
  );

  // Global filter: controlled value > initial > empty
  const [uncontrolledGlobalFilter, setUncontrolledGlobalFilter] = useState<string>(
    () => globalFilterConfig.initial ?? ''
  );

  // Row selection: controlled value > initial > empty
  const [uncontrolledSelection, setUncontrolledSelection] = useState<RowSelectionState>(
    () => selection.initial ?? {}
  );

  // Column sizing with optional cache
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(() =>
    settings.cache.sizing ? (loadFromCache<ColumnSizingState>(tableId, 'sizing') ?? {}) : {}
  );

  const [columnSizingInfo, setColumnSizingInfo] = useState<ColumnSizingInfoState>({
    startOffset: null,
    startSize: null,
    deltaOffset: null,
    deltaPercentage: null,
    isResizingColumn: false,
    columnSizingStart: [],
  });

  // ---------------------------------------------------------------------------
  // Effective values (controlled wins over uncontrolled)
  // ---------------------------------------------------------------------------

  const sorting = isSortingControlled ? sortingConfig.value! : uncontrolledSorting;
  const columnFilters = isFilterControlled ? filter.value! : uncontrolledFilters;
  const globalFilter = isGlobalFilterControlled
    ? globalFilterConfig.value!
    : uncontrolledGlobalFilter;
  const rowSelection = isSelectionControlled ? selection.value! : uncontrolledSelection;

  // Column overlay state
  const [overlayColumnIds, setOverlayColumnIds] = useState<string[]>([]);
  const [overlayHeaderClassName, setOverlayHeaderClassName] =
    useState<TableContextValue['overlayHeaderClassName']>(undefined);
  const overlayHeaderClassNameRef = useRef<TableContextValue['overlayHeaderClassName']>(undefined);

  // Updater to set overlay columns and header styling
  const updateOverlayColumns = useCallback(
    (columnIds: string[], headerClassName?: TableContextValue['overlayHeaderClassName']) => {
      setOverlayColumnIds((previous) => {
        if (
          previous.length === columnIds.length &&
          previous.every((columnId, index) => columnId === columnIds[index])
        ) {
          return previous;
        }
        return columnIds;
      });
      if (overlayHeaderClassNameRef.current !== headerClassName) {
        overlayHeaderClassNameRef.current = headerClassName;
        if (typeof headerClassName === 'function') {
          // Store resolver function as state value (not as React setState updater).
          setOverlayHeaderClassName(() => headerClassName);
        } else {
          setOverlayHeaderClassName(headerClassName);
        }
      }
    },
    []
  );

  // Scroll-to-column
  const scrollToColumn = useScrollToColumn(scrollContainerRef, tableRef, columns as ColumnConfig[]);

  // Scroll-to-column effect: react to columnId changes from DataTable.ScrollTo
  const prevScrollToColumnIdRef = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    if (!scrollTo) return;
    const { columnId, align, offset } = scrollTo;
    // Only scroll on transitions to a new non-null columnId
    if (columnId && columnId !== prevScrollToColumnIdRef.current) {
      scrollToColumn(columnId, { align, offset });
    }
    prevScrollToColumnIdRef.current = columnId ?? null;
  }, [scrollTo, scrollToColumn]);

  // ---------------------------------------------------------------------------
  // Change handlers
  // ---------------------------------------------------------------------------

  const handleSortingChange = useCallback(
    (updater: SortingState | ((old: SortingState) => SortingState)) => {
      const nextValue = typeof updater === 'function' ? updater(sorting) : updater;

      // Update internal state if uncontrolled
      if (!isSortingControlled) {
        setUncontrolledSorting(nextValue);
        if (settings.cache.sorting) {
          saveToCache(tableId, 'sorting', nextValue);
        }
      }

      // Notify parent
      sortingConfig.onChange?.(nextValue);
    },
    [sorting, isSortingControlled, tableId, settings.cache.sorting, sortingConfig]
  );

  const handleFiltersChange = useCallback(
    (updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => {
      const nextValue = typeof updater === 'function' ? updater(columnFilters) : updater;

      // Update internal state if uncontrolled
      if (!isFilterControlled) {
        setUncontrolledFilters(nextValue);
      }

      // Notify parent
      filter.onChange?.(nextValue);
    },
    [columnFilters, isFilterControlled, filter]
  );

  const handleGlobalFilterChange = useCallback(
    (updater: string | ((old: string) => string)) => {
      const nextValue = typeof updater === 'function' ? updater(globalFilter) : updater;

      // Update internal state if uncontrolled
      if (!isGlobalFilterControlled) {
        setUncontrolledGlobalFilter(nextValue);
      }

      // Notify parent
      globalFilterConfig.onChange?.(nextValue);
    },
    [globalFilter, isGlobalFilterControlled, globalFilterConfig]
  );

  /**
   * Default global filter: case-insensitive substring match across all
   * visible columns' cell values. Empty / whitespace input is a no-op.
   * Consumers can override via DataTable.GlobalFilter `filterFn` prop.
   */
  const globalFilterFn = useMemo(() => {
    if (globalFilterConfig.filterFn) {
      return globalFilterConfig.filterFn as (
        row: Row<TData>,
        columnId: string,
        filterValue: string
      ) => boolean;
    }

    return (row: Row<TData>, _columnId: string, filterValue: string): boolean => {
      const needle = String(filterValue ?? '')
        .trim()
        .toLowerCase();
      if (!needle) return true;

      for (const cell of row.getVisibleCells()) {
        const value = cell.getValue();
        if (value === null || value === undefined) continue;
        if (String(value).toLowerCase().includes(needle)) return true;
      }
      return false;
    };
  }, [globalFilterConfig.filterFn]);

  const handleColumnSizingChange = useCallback(
    (updater: ColumnSizingState | ((old: ColumnSizingState) => ColumnSizingState)) => {
      setColumnSizing((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (settings.cache.sizing) {
          saveToCache(tableId, 'sizing', next);
        }
        return next;
      });
    },
    [tableId, settings.cache.sizing]
  );

  // Direct state updater for enhanced sizing sync (merges with existing sizes)
  const updateColumnSizing = useCallback(
    (sizes: ColumnSizingState) => {
      setColumnSizing((prev) => {
        // Only update if values actually changed to avoid unnecessary re-renders
        const hasChanges = Object.entries(sizes).some(([id, size]) => prev[id] !== size);
        if (!hasChanges) return prev;

        const next = { ...prev, ...sizes };
        if (settings.cache.sizing) {
          saveToCache(tableId, 'sizing', next);
        }
        return next;
      });
    },
    [tableId, settings.cache.sizing]
  );

  const handleRowSelectionChange = useCallback(
    (updater: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      const nextValue = typeof updater === 'function' ? updater(rowSelection) : updater;

      // Update internal state if uncontrolled
      if (!isSelectionControlled) {
        setUncontrolledSelection(nextValue);
      }

      // Notify parent (controlled mode)
      selection.onChange?.(nextValue);
    },
    [rowSelection, isSelectionControlled, selection]
  );

  // ---------------------------------------------------------------------------
  // Build Column Definitions
  // ---------------------------------------------------------------------------

  const columnDefs = useMemo(
    () => buildColumnDefs(columns, effectiveDefaultColumnSize),
    [columns, effectiveDefaultColumnSize]
  );

  // ---------------------------------------------------------------------------
  // Column Visibility (for hidden columns that still participate in sorting)
  // ---------------------------------------------------------------------------

  const columnVisibility = useMemo<VisibilityState>(() => {
    const visibility: VisibilityState = {};
    for (const col of columns) {
      if (col.hidden) visibility[col.id] = false;
    }
    return visibility;
  }, [columns]);

  // Pinning state derived from declarative column config (enables getStart/getAfter).
  // Hidden columns are excluded so they do not affect getAfter offsets for visible pins.
  // Right-pinned columns must be declared as a contiguous trailing group in column order.
  const columnPinning = useMemo<ColumnPinningState>(() => {
    const left: string[] = [];
    const right: string[] = [];
    for (const col of columns) {
      if (col.hidden) {
        continue;
      }
      if (col.pinned === 'left') {
        left.push(col.id);
      } else if (col.pinned === 'right') {
        right.push(col.id);
      }
    }
    return { left, right };
  }, [columns]);

  // ---------------------------------------------------------------------------
  // Create TanStack Table
  // ---------------------------------------------------------------------------

  const table: Table<TData> = useReactTable({
    data,
    columns: columnDefs,

    // State
    state: {
      sorting,
      columnFilters,
      globalFilter,
      rowSelection,
      columnSizing,
      columnSizingInfo,
      columnVisibility,
      columnPinning,
    },

    // State handlers
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleFiltersChange,
    onGlobalFilterChange: handleGlobalFilterChange,
    onRowSelectionChange: handleRowSelectionChange,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnSizingInfoChange: setColumnSizingInfo,

    // Global filter
    globalFilterFn,

    // Row models
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    // Row selection config - supports per-row control via function
    enableRowSelection: selection.enableRowSelection
      ? (row) => selection.enableRowSelection!(row.original)
      : selection.enabled,
    enableMultiRowSelection: selection.enableMultiSelect ?? true,
    // Stable row IDs are critical for virtualization and sorting.
    // Provided via the getRowId prop on DataTable.
    getRowId: getRowIdProp ? (row: TData, index: number) => getRowIdProp(row, index) : undefined,

    // Column sizing config
    columnResizeMode: 'onChange',

    // Table meta
    meta: {
      tableId,
      isMobile,
      tableRef,
    },
  });

  // ---------------------------------------------------------------------------
  // Return
  // ---------------------------------------------------------------------------

  return useMemo(
    (): UseTableStateResult<TData> => ({
      contextValue: {
        tableId,
        table,
        isMobile,
        widthMode,
        tableRef,
        defaultColumnSize: effectiveDefaultColumnSize,
        density: settings.density,
        stickyHeader: settings.stickyHeader,
        styles: settings.styles,
        dragScroll: settings.dragScroll ?? false,
        settings,
        columnConfigs: columns,
        sorting,
        columnFilters,
        globalFilter,
        rowSelection,
        columnSizing,
        columnSizingInfo,
        overlayColumnIds,
        overlayHeaderClassName,
        scrollContainerRef,
        scrollToColumn,
        updateColumnSizing,
        updateOverlayColumns,
        emptyMessage,
      },
      table,
    }),
    [
      tableId,
      table,
      isMobile,
      widthMode,
      tableRef,
      effectiveDefaultColumnSize,
      settings,
      columns,
      sorting,
      columnFilters,
      globalFilter,
      rowSelection,
      columnSizing,
      columnSizingInfo,
      overlayColumnIds,
      overlayHeaderClassName,
      scrollContainerRef,
      scrollToColumn,
      updateColumnSizing,
      updateOverlayColumns,
      emptyMessage,
    ]
  );
};
